import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  CheckCheck,
  CheckCircle2,
  ChevronDown,
  Clock3,
  ExternalLink,
  Hash,
  Inbox,
  LifeBuoy,
  Mail,
  MapPin,
  MessagesSquare,
  Package,
  RotateCcw,
  Search,
  SendHorizontal,
  Tag,
  Video,
} from "lucide-react";
import { useAppServices } from "../../hook/services";
import { cx, formatDate, gradientFor, initials } from "../../utils";
import { useToast } from "../../components/ui/Toast";
import Pagination from "../../components/ui/Pagination";
import useListQuery, { useSearchInput } from "../../hook/useListQuery";

/**
 * The support inbox: every ticket from every app page, answered live.
 *
 * Three panes filling the window — tickets, the conversation, the customer —
 * each scrolling on its own, so the composer and the ticket controls never
 * scroll out of reach. New tickets and replies arrive over a Server-Sent
 * Events stream. Replying moves an open ticket to "Waiting on customer"; a
 * customer reply moves it back to Open.
 */

const STATUSES = [
  { id: "open", label: "Open", dot: "bg-brand-500", pill: "bg-brand-50 text-brand-700 ring-brand-200/70" },
  { id: "pending", label: "Waiting on customer", short: "Waiting", dot: "bg-amber-500", pill: "bg-amber-50 text-amber-700 ring-amber-200/80" },
  { id: "resolved", label: "Resolved", dot: "bg-emerald-500", pill: "bg-emerald-50 text-emerald-700 ring-emerald-200/80" },
  { id: "closed", label: "Closed", dot: "bg-ink-400", pill: "bg-ink-100 text-ink-600 ring-ink-200/80" },
];
const statusOf = (id) => STATUSES.find((s) => s.id === id) || STATUSES[0];

const PRIORITIES = [
  { id: "low", label: "Low", tone: "text-ink-500" },
  { id: "normal", label: "Normal", tone: "text-ink-700" },
  { id: "high", label: "High", tone: "text-amber-700" },
  { id: "urgent", label: "Urgent", tone: "text-rose-600" },
];

/* ------------------------------------------------------------------ time */

const sameDay = (a, b) => a.toDateString() === b.toDateString();

const ago = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 7 * 86400) return d.toLocaleDateString(undefined, { weekday: "short" });
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const clock = (value) => new Date(value).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

const dayLabel = (value) => {
  const d = new Date(value);
  if (sameDay(d, new Date())) return "Today";
  if (sameDay(d, new Date(Date.now() - 86400000))) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
};

const upsert = (list, ticket) =>
  [ticket, ...list.filter((t) => t._id !== ticket._id)].sort(
    (a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt),
  );

// Day dividers, and consecutive messages from the same person within five
// minutes run together under one name.
const groupMessages = (messages) => {
  const out = [];
  messages.forEach((m, i) => {
    const prev = messages[i - 1];
    const d = new Date(m.createdAt);
    if (!prev || !sameDay(new Date(prev.createdAt), d)) out.push({ kind: "day", key: `d-${m._id}`, label: dayLabel(m.createdAt) });
    const last = out[out.length - 1];
    const joins =
      last?.kind === "group" &&
      last.from === m.from &&
      last.authorName === m.authorName &&
      d - new Date(last.messages[last.messages.length - 1].createdAt) < 5 * 60 * 1000;
    if (joins) last.messages.push(m);
    else out.push({ kind: "group", key: `g-${m._id}`, from: m.from, authorName: m.authorName, messages: [m] });
  });
  return out;
};

const customerName = (t) => t?.submittedBy?.userName || t?.locationName || t?.companyName || "Customer";

// The generic "Customer" a message falls back to when SSO gave no name says
// less than the account it came from.
const authorOf = (group, ticket) =>
  group.authorName && group.authorName !== "Customer" ? group.authorName : customerName(ticket);

/* ------------------------------------------------------------- pieces */

const Avatar = ({ name, size = "h-9 w-9 text-[12px]", ring }) => (
  <span
    className={cx(
      "grid shrink-0 place-items-center rounded-full bg-gradient-to-br font-semibold text-white shadow-sm",
      gradientFor(name || "?"),
      size,
      ring,
    )}
    aria-hidden
  >
    {initials(name || "?")}
  </span>
);

const StatusPill = ({ status }) => {
  const s = statusOf(status);
  return (
    <span className={cx("inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1", s.pill)}>
      <span className={cx("h-1.5 w-1.5 rounded-full", s.dot)} />
      {s.short || s.label}
    </span>
  );
};

const Chip = ({ icon: Icon, children, mono }) => (
  <span className="inline-flex min-w-0 max-w-[14rem] items-center gap-1 rounded-md bg-ink-100/80 px-1.5 py-0.5 text-[11px] font-medium text-ink-600">
    {Icon && <Icon size={11} className="shrink-0 text-ink-400" aria-hidden />}
    {/* One line: a long app name is cut with "…" rather than wrapping the header. */}
    <span className={cx("truncate whitespace-nowrap", mono && "font-mono tabular-nums")}>{children}</span>
  </span>
);

// A small menu that opens from a pill: status in the conversation header.
const StatusMenu = ({ status, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const s = statusOf(status);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => !ref.current?.contains(e.target) && setOpen(false);
    const esc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cx(
          "inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-[12px] font-medium ring-1 transition-shadow hover:shadow-sm",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
          s.pill,
        )}
      >
        <span className={cx("h-1.5 w-1.5 rounded-full", s.dot)} />
        {s.label}
        <ChevronDown size={13} className={cx("transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label="Ticket status"
          className="absolute right-0 top-10 z-20 w-52 animate-fade-in overflow-hidden rounded-xl border border-ink-200/80 bg-white p-1 shadow-card-elevated"
        >
          {STATUSES.map((x) => (
            <li key={x.id}>
              <button
                type="button"
                role="option"
                aria-selected={x.id === status}
                onClick={() => {
                  setOpen(false);
                  if (x.id !== status) onChange(x.id);
                }}
                className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] text-ink-700 hover:bg-ink-50"
              >
                <span className={cx("h-2 w-2 rounded-full", x.dot)} />
                <span className="flex-1">{x.label}</span>
                {x.id === status && <Check size={14} className="text-brand-600" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Row = ({ icon: Icon, label, children }) => (
  <div className="flex gap-3 py-2">
    <Icon size={15} className="mt-0.5 shrink-0 text-ink-400" aria-hidden />
    <div className="min-w-0 flex-1">
      <p className="text-[11px] font-medium uppercase tracking-wider text-ink-400">{label}</p>
      <div className="mt-0.5 text-[13px] text-ink-800">{children}</div>
    </div>
  </div>
);

/* -------------------------------------------------------------- composer */

const Composer = ({ onSend, customer }) => {
  const [draft, setDraft] = useState("");
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }, [draft]);

  const send = () => {
    const body = draft.trim();
    if (!body) return;
    setDraft(""); // the message shows straight away; a failure puts it back
    onSend(body, () => setDraft(body));
    ref.current?.focus();
  };

  const ready = draft.trim().length > 0;

  return (
    <div className="border-t border-ink-150 bg-white px-5 py-4">
      <div className="rounded-2xl border border-ink-200 bg-white shadow-sm transition-all focus-within:border-brand-300 focus-within:shadow-glow">
        <textarea
          ref={ref}
          rows={1}
          value={draft}
          maxLength={5000}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder={`Reply to ${customer}…`}
          aria-label="Reply"
          className="block max-h-44 min-h-[2.75rem] w-full resize-none rounded-t-2xl border-0 bg-transparent px-4 pb-1 pt-3 text-[14px] leading-6 text-ink-900 placeholder:text-ink-400 focus:outline-none focus:ring-0"
        />
        <div className="flex items-center justify-between gap-3 px-3 pb-2.5 pt-1">
          <p className="flex items-center gap-1.5 pl-1 text-[11.5px] text-ink-400">
            <kbd className="rounded border border-ink-200 bg-ink-50 px-1.5 py-px font-sans text-[10.5px] font-medium text-ink-500">Enter</kbd>
            to send
            <span className="text-ink-300">·</span>
            <kbd className="rounded border border-ink-200 bg-ink-50 px-1.5 py-px font-sans text-[10.5px] font-medium text-ink-500">Shift + Enter</kbd>
            new line
          </p>
          <button
            type="button"
            onClick={send}
            disabled={!ready}
            className={cx(
              "inline-flex h-9 items-center gap-1.5 rounded-xl px-4 text-[13px] font-semibold transition-all",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
              ready
                ? "bg-gradient-to-br from-brand-500 to-violet-600 text-white shadow-brand-glow hover:brightness-110"
                : "bg-ink-100 text-ink-400",
            )}
          >
            Send <SendHorizontal size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ page */

const ALL = "all";

const FILTER_SELECT =
  "h-8 cursor-pointer rounded-lg border border-ink-200 bg-white px-2 text-[12px] text-ink-700 transition-colors hover:border-ink-300 focus:border-brand-300 focus:outline-none focus:shadow-glow";

const DEFAULTS = { page: 1, limit: 25, status: "open", appId: "", priority: "", unread: "", q: "" };

const Support = () => {
  const { support, apps: appsService } = useAppServices();
  const toast = useToast();

  // Paged and filtered on the server; the address holds the current view.
  const [query, setQuery] = useListQuery(DEFAULTS);
  const [search, setSearch] = useSearchInput(query.q, (q) => setQuery({ q }));

  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1, limit: DEFAULTS.limit, counts: {}, unread: 0 });
  const [loading, setLoading] = useState(true);
  const [apps, setApps] = useState([]);
  // The open ticket is held on its own, so it stays open when a status change
  // or another page takes it out of the list.
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [live, setLive] = useState(false);
  const endRef = useRef(null);

  const selectedId = selected?._id || null;
  const selectedRef = useRef(null);
  selectedRef.current = selectedId;

  const tickets = data.items;

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page: query.page, limit: query.limit });
    if (query.status !== ALL) params.set("status", query.status);
    for (const key of ["appId", "priority", "unread", "q"]) if (query[key]) params.set(key, query[key]);

    const { response, error } = await support.List(params.toString());
    if (error) toast(error?.message || error?.error || "Could not load tickets", "error");
    const next = response && Array.isArray(response.items) ? response : { items: [], total: 0, page: 1, pages: 1, counts: {}, unread: 0 };
    if (next.items.length === 0 && next.total > 0 && query.page > next.pages) setQuery({ page: next.pages });
    setData(next);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.page, query.limit, query.status, query.appId, query.priority, query.unread, query.q]);

  useEffect(() => {
    load();
  }, [load]);

  // Live events reload the current page once things settle, so order, counts
  // and the unread total stay true without a request per event.
  const loadRef = useRef(load);
  loadRef.current = load;
  const refreshTimer = useRef(null);
  const refreshSoon = useCallback(() => {
    clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => loadRef.current(), 600);
  }, []);
  useEffect(() => () => clearTimeout(refreshTimer.current), []);

  useEffect(() => {
    appsService.Get().then(({ response }) =>
      setApps(
        (Array.isArray(response) ? response : [])
          .filter((a) => a.appId)
          .map((a) => [a.appId, a.appName || a.appId])
          .sort((a, b) => a[1].localeCompare(b[1])),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A ticket changed: show it in the list (if it's on this page) and in the
  // conversation pane (if it's the open one).
  const applyTicket = useCallback((ticket) => {
    if (!ticket?._id) return;
    setData((prev) =>
      prev.items.some((t) => t._id === ticket._id) ? { ...prev, items: upsert(prev.items, ticket) } : prev,
    );
    setSelected((cur) => (cur && cur._id === ticket._id ? ticket : cur));
  }, []);

  const open = useCallback(async (ticket) => {
    setSelected(ticket);
    setMessages([]);
    setLoadingThread(true);
    const { response } = await support.Get(ticket._id);
    if (selectedRef.current !== ticket._id) return;
    setMessages(response?.messages || []);
    if (response?.ticket) applyTicket(response.ticket);
    setLoadingThread(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live: new tickets, customer replies, and changes made in another tab.
  useEffect(() => {
    if (typeof EventSource === "undefined") return undefined;
    const source = new EventSource(support.streamUrl());

    source.addEventListener("ready", () => setLive(true));
    source.onerror = () => setLive(false);

    source.addEventListener("ticket", (e) => {
      const { ticket } = JSON.parse(e.data);
      if (!ticket) return;
      applyTicket(ticket);
      refreshSoon();
    });

    source.addEventListener("message", (e) => {
      const { ticket, message } = JSON.parse(e.data);
      if (!ticket) return;
      const isOpen = selectedRef.current === ticket._id;
      applyTicket(isOpen ? { ...ticket, unreadForAdmin: 0 } : ticket);
      refreshSoon();

      if (isOpen && message) {
        setMessages((prev) => (prev.some((m) => m._id === message._id) ? prev : [...prev, message]));
        if (message.from === "customer") support.Read(ticket._id);
      } else if (message?.from === "customer") {
        toast(ticket.messageCount === 1 ? `New ticket #${ticket.number}: ${ticket.subject}` : `New reply on #${ticket.number}`);
      }
    });

    return () => source.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
  }, [messages.length, selectedId]);

  // Per-status totals under the other filters, from the server.
  const counts = useMemo(() => {
    const c = Object.fromEntries(STATUSES.map((s) => [s.id, data.counts?.[s.id] || 0]));
    return { ...c, [ALL]: STATUSES.reduce((n, s) => n + c[s.id], 0) };
  }, [data.counts]);

  const visible = tickets;
  const tab = query.status;
  const filtered = Boolean(query.q || query.appId || query.priority || query.unread);
  const clearFilters = () => setQuery({ q: "", appId: "", priority: "", unread: "" });

  const groups = useMemo(() => groupMessages(messages), [messages]);
  const unreadTotal = data.unread || 0;

  // Shown at once with a "Sending…" state, swapped for the saved message when
  // the server confirms it; on failure it's removed and the text put back.
  const sendReply = async (body, restore) => {
    if (!selected) return;
    const temp = { _id: `tmp-${Date.now()}`, from: "admin", authorName: "You", body, createdAt: new Date().toISOString(), pending: true };
    setMessages((prev) => [...prev, temp]);

    const { response, error } = await support.Reply(selected._id, body);
    if (error) {
      setMessages((prev) => prev.filter((m) => m._id !== temp._id));
      restore();
      toast(error?.message || "Could not send the reply", "error");
      return;
    }
    applyTicket(response.ticket);
    refreshSoon();
    setMessages((prev) => {
      const without = prev.filter((m) => m._id !== temp._id);
      return without.some((m) => m._id === response.message._id) ? without : [...without, response.message];
    });
  };

  const update = async (patch, note) => {
    if (!selected) return;
    const { response, error } = await support.Update(selected._id, patch);
    if (error) {
      toast(error?.message || "Could not update the ticket", "error");
      return;
    }
    applyTicket(response);
    refreshSoon();
    toast(note || "Ticket updated");
  };

  const filters = [...STATUSES.map((s) => ({ id: s.id, label: s.short || s.label })), { id: ALL, label: "All" }];
  const who = selected?.submittedBy || {};
  const finished = selected && (selected.status === "resolved" || selected.status === "closed");
  const lastGroup = [...groups].reverse().find((g) => g.kind === "group");

  return (
    <div className="flex h-[calc(100vh-6.5rem)] min-h-[34rem] overflow-hidden rounded-2xl border border-ink-200/70 bg-white shadow-card-elevated">
      {/* ---------------- Tickets ---------------- */}
      <aside className="flex w-[21rem] shrink-0 flex-col border-r border-ink-150">
        <div className="space-y-3 border-b border-ink-150 px-4 pb-3 pt-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-baseline gap-2">
              <h2 className="text-[16px] font-semibold tracking-tight text-ink-900">Inbox</h2>
              {unreadTotal > 0 && (
                <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-semibold text-brand-700 ring-1 ring-brand-200/70">
                  {unreadTotal} unread
                </span>
              )}
            </div>
            <span className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-ink-500" title={live ? "New tickets and replies appear instantly" : "Reconnecting…"}>
              <span className="relative flex h-2 w-2">
                {live && <span className="absolute inline-flex h-full w-full animate-ping-slow rounded-full bg-emerald-400" />}
                <span className={cx("relative inline-flex h-2 w-2 rounded-full", live ? "bg-emerald-500" : "bg-ink-300")} />
              </span>
              {live ? "Live" : "Connecting"}
            </span>
          </div>

          <div className="relative">
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tickets, people, accounts"
              aria-label="Search tickets"
              className="h-9 w-full rounded-xl border border-ink-200 bg-ink-50/60 pl-9 pr-3 text-[13px] text-ink-900 placeholder:text-ink-400 transition-colors focus:border-brand-300 focus:bg-white focus:outline-none focus:shadow-glow"
            />
          </div>

          <div className="flex items-center gap-1.5">
            <select
              value={query.appId}
              onChange={(e) => setQuery({ appId: e.target.value })}
              aria-label="Filter by app"
              className={cx(FILTER_SELECT, "min-w-0 flex-1", query.appId && "border-brand-300 bg-brand-50/60 text-brand-800")}
            >
              <option value="">All apps</option>
              {apps.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
            <select
              value={query.priority}
              onChange={(e) => setQuery({ priority: e.target.value })}
              aria-label="Filter by priority"
              className={cx(FILTER_SELECT, "w-[6.75rem] shrink-0", query.priority && "border-brand-300 bg-brand-50/60 text-brand-800")}
            >
              <option value="">Any priority</option>
              {PRIORITIES.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              aria-pressed={Boolean(query.unread)}
              onClick={() => setQuery({ unread: query.unread ? "" : "1" })}
              className={cx(
                "h-8 shrink-0 rounded-lg border px-2.5 text-[12px] font-medium transition-colors",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500",
                query.unread ? "border-brand-300 bg-brand-50/60 text-brand-800" : "border-ink-200 bg-white text-ink-600 hover:bg-ink-50",
              )}
            >
              Unread
            </button>
          </div>

          <div className="flex rounded-xl bg-ink-100/80 p-1" role="tablist" aria-label="Filter by status">
            {filters.map((f) => (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={tab === f.id}
                onClick={() => setQuery({ status: f.id })}
                className={cx(
                  "flex-1 rounded-lg px-1 py-1.5 text-center text-[11.5px] font-medium transition-all",
                  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500",
                  tab === f.id ? "bg-white text-ink-900 shadow-sm" : "text-ink-500 hover:text-ink-800",
                )}
              >
                {f.label}
                <span className={cx("ml-1 tabular-nums", tab === f.id ? "text-brand-600" : "text-ink-400")}>{counts[f.id] || 0}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading ? (
            <div className="space-y-1 p-2">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="flex gap-3 rounded-xl p-3">
                  <div className="h-9 w-9 animate-pulse rounded-full bg-ink-100" />
                  <div className="flex-1 space-y-2 pt-1">
                    <div className="h-3 w-1/2 animate-pulse rounded bg-ink-100" />
                    <div className="h-3 w-4/5 animate-pulse rounded bg-ink-100" />
                  </div>
                </div>
              ))}
            </div>
          ) : visible.length === 0 ? (
            <div className="px-8 py-16 text-center">
              <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-brand-subtle text-brand-600 ring-1 ring-brand-200/60">
                <Inbox size={20} />
              </span>
              <p className="mt-4 text-[13.5px] font-semibold text-ink-800">
                {counts[ALL] === 0 && !filtered ? "No tickets yet" : filtered ? "No matches" : "All clear"}
              </p>
              <p className="mt-1 text-[12.5px] leading-relaxed text-ink-500">
                {counts[ALL] === 0 && !filtered
                  ? "Tickets opened from any app page will land here."
                  : filtered
                    ? "Try a different name, number or filter."
                    : "Nothing in this view right now."}
              </p>
              {filtered && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="mt-4 inline-flex h-8 items-center rounded-lg border border-ink-200 bg-white px-3 text-[12.5px] font-medium text-ink-700 shadow-sm hover:bg-ink-50"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <ul className="p-2">
              {visible.map((t) => {
                const active = t._id === selectedId;
                const name = customerName(t);
                const unread = t.unreadForAdmin > 0;
                return (
                  <li key={t._id}>
                    <button
                      type="button"
                      onClick={() => open(t)}
                      className={cx(
                        "relative flex w-full gap-3 rounded-xl px-3 py-3 text-left transition-colors",
                        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500",
                        active ? "bg-brand-50/70" : "hover:bg-ink-50",
                      )}
                    >
                      {active && <span className="absolute inset-y-3 left-0 w-[3px] rounded-r-full bg-brand-600" />}
                      <Avatar name={name} />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className={cx("min-w-0 flex-1 truncate text-[13px]", unread ? "font-semibold text-ink-900" : "font-medium text-ink-700")}>
                            {name}
                          </span>
                          <span className={cx("shrink-0 text-[11px]", unread ? "font-semibold text-brand-600" : "text-ink-400")}>
                            {ago(t.lastMessageAt)}
                          </span>
                        </span>
                        <span className={cx("mt-0.5 block truncate text-[13px]", unread ? "font-semibold text-ink-900" : "text-ink-800")}>
                          {t.subject}
                        </span>
                        <span className="mt-0.5 flex items-center gap-2">
                          <span className="min-w-0 flex-1 truncate text-[12.5px] text-ink-500">
                            {t.lastMessageFrom === "admin" && <span className="text-ink-400">You: </span>}
                            {t.lastMessagePreview}
                          </span>
                          {unread && (
                            <span className="grid h-5 min-w-[20px] shrink-0 place-items-center rounded-full bg-brand-600 px-1.5 text-[10.5px] font-semibold text-white">
                              {t.unreadForAdmin}
                            </span>
                          )}
                        </span>
                        <span className="mt-2 flex flex-wrap items-center gap-1.5">
                          <StatusPill status={t.status} />
                          {(t.priority === "high" || t.priority === "urgent") && (
                            <span className={cx("rounded-full px-2 py-0.5 text-[10.5px] font-semibold ring-1", t.priority === "urgent" ? "bg-rose-50 text-rose-600 ring-rose-200/80" : "bg-amber-50 text-amber-700 ring-amber-200/80")}>
                              {t.priority === "urgent" ? "Urgent" : "High"}
                            </span>
                          )}
                          {t.loomUrl && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[10.5px] font-medium text-violet-700 ring-1 ring-violet-200/70">
                              <Video size={10} /> Video
                            </span>
                          )}
                          <span className="ml-auto font-mono text-[10.5px] text-ink-400">#{t.number}</span>
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {!loading && (
          <Pagination
            compact
            page={data.page}
            pages={data.pages}
            total={data.total}
            limit={data.limit}
            onPage={(page) => setQuery({ page })}
          />
        )}
      </aside>

      {/* ---------------- Conversation ---------------- */}
      <section className="flex min-w-0 flex-1 flex-col">
        {selected ? (
          <>
            <header className="flex items-center gap-4 border-b border-ink-150 px-6 py-4">
              <Avatar name={customerName(selected)} size="h-10 w-10 text-[13px]" />
              <div className="min-w-0 flex-1">
                <h3 className="line-clamp-2 text-[15.5px] font-semibold leading-snug tracking-tight text-ink-900" title={selected.subject}>{selected.subject}</h3>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  <Chip icon={Hash} mono>{selected.number}</Chip>
                  <Chip icon={Tag}>{selected.category}</Chip>
                  <Chip icon={Package}>{selected.appName}</Chip>
                </div>
              </div>
              <StatusMenu status={selected.status} onChange={(status) => update({ status }, `Moved to ${statusOf(status).label}`)} />
              {finished ? (
                <button
                  type="button"
                  onClick={() => update({ status: "open" }, "Ticket reopened")}
                  className="inline-flex h-8 items-center gap-1.5 rounded-full border border-ink-200 bg-white px-3 text-[12px] font-medium text-ink-700 transition-colors hover:bg-ink-50"
                >
                  <RotateCcw size={13} /> Reopen
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => update({ status: "resolved" }, "Marked as resolved")}
                  className="inline-flex h-8 items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 text-[12px] font-medium text-emerald-700 transition-colors hover:bg-emerald-100"
                >
                  <CheckCircle2 size={13} /> Resolve
                </button>
              )}
            </header>

            <div className="relative min-h-0 flex-1 overflow-y-auto bg-ink-50/70">
              {/* A faint dot grid gives the canvas texture without competing with the messages. */}
              <div className="pointer-events-none absolute inset-0 bg-dotted bg-dot-md opacity-60" aria-hidden />
              <div className="relative mx-auto max-w-3xl space-y-5 px-6 py-6">
                {loadingThread ? (
                  <div className="space-y-5">
                    <div className="flex items-end gap-3">
                      <div className="h-8 w-8 animate-pulse rounded-full bg-ink-200/70" />
                      <div className="h-16 w-1/2 animate-pulse rounded-2xl bg-white" />
                    </div>
                    <div className="flex justify-end">
                      <div className="h-12 w-2/5 animate-pulse rounded-2xl bg-brand-100" />
                    </div>
                  </div>
                ) : (
                  groups.map((g) =>
                    g.kind === "day" ? (
                      <div key={g.key} className="flex justify-center" role="separator">
                        <span className="rounded-full bg-white px-3 py-1 text-[11px] font-medium text-ink-500 shadow-sm ring-1 ring-ink-200/70">
                          {g.label}
                        </span>
                      </div>
                    ) : g.from === "admin" ? (
                      <div key={g.key} className="flex flex-col items-end gap-1">
                        {g.messages.map((m, i) => (
                          <div
                            key={m._id}
                            className={cx(
                              "max-w-[72%] animate-fade-in whitespace-pre-line break-words rounded-2xl bg-gradient-to-br from-brand-500 to-violet-600 px-4 py-2.5 text-[14px] leading-relaxed text-white shadow-sm",
                              i < g.messages.length - 1 && "rounded-br-md",
                              i > 0 && "rounded-tr-md",
                              m.pending && "opacity-70",
                            )}
                          >
                            {m.body}
                          </div>
                        ))}
                        <span className="flex items-center gap-1 px-1 text-[11px] text-ink-400">
                          {g.messages[g.messages.length - 1].pending ? (
                            <>
                              <Clock3 size={11} /> Sending…
                            </>
                          ) : (
                            <>
                              {g.authorName && g.authorName !== "You" ? g.authorName : "You"} · {clock(g.messages[g.messages.length - 1].createdAt)}
                              {g === lastGroup &&
                                (selected.unreadForCustomer === 0 ? (
                                  <span className="ml-1 inline-flex items-center gap-0.5 font-medium text-brand-600">
                                    <CheckCheck size={13} /> Seen
                                  </span>
                                ) : (
                                  <span className="ml-1 inline-flex items-center gap-0.5">
                                    <Check size={13} /> Delivered
                                  </span>
                                ))}
                            </>
                          )}
                        </span>
                      </div>
                    ) : (
                      <div key={g.key} className="flex items-end gap-3">
                        <Avatar name={authorOf(g, selected)} size="h-8 w-8 text-[11px]" />
                        <div className="flex min-w-0 max-w-[72%] flex-col items-start gap-1">
                          <span className="px-1 text-[11.5px] font-semibold text-ink-700">{authorOf(g, selected)}</span>
                          {g.messages.map((m, i) => (
                            <div
                              key={m._id}
                              className={cx(
                                "max-w-full animate-fade-in whitespace-pre-line break-words rounded-2xl bg-white px-4 py-2.5 text-[14px] leading-relaxed text-ink-800 shadow-sm ring-1 ring-ink-200/70",
                                i < g.messages.length - 1 && "rounded-bl-md",
                                i > 0 && "rounded-tl-md",
                              )}
                            >
                              {m.body}
                            </div>
                          ))}
                          <span className="px-1 text-[11px] text-ink-400">{clock(g.messages[g.messages.length - 1].createdAt)}</span>
                        </div>
                      </div>
                    ),
                  )
                )}
                <div ref={endRef} />
              </div>
            </div>

            <Composer onSend={sendReply} customer={customerName(selected)} />
          </>
        ) : (
          <div className="relative grid h-full place-items-center overflow-hidden bg-ink-50/70 p-8 text-center">
            <div className="pointer-events-none absolute inset-0 bg-dotted bg-dot-md opacity-60" aria-hidden />
            <div className="relative max-w-sm">
              <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 to-violet-600 text-white shadow-brand-glow">
                <MessagesSquare size={24} />
              </span>
              <h3 className="mt-5 text-[16px] font-semibold tracking-tight text-ink-900">Select a conversation</h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-ink-500">
                Pick a ticket from the inbox to read it and reply. New tickets and replies show up here the moment they arrive.
              </p>
            </div>
          </div>
        )}
      </section>

      {/* ---------------- Customer & ticket ---------------- */}
      {selected && (
        <aside className="hidden w-[19rem] shrink-0 flex-col overflow-y-auto border-l border-ink-150 bg-white xl:flex">
          <div className="border-b border-ink-150 bg-brand-subtle px-5 pb-5 pt-6 text-center">
            <Avatar name={customerName(selected)} size="mx-auto h-16 w-16 text-[18px]" ring="ring-4 ring-white" />
            <p className="mt-3 text-[15px] font-semibold tracking-tight text-ink-900">{customerName(selected)}</p>
            {who.role && <p className="mt-0.5 text-[12px] capitalize text-ink-500">{who.role}</p>}
            {who.email && (
              <a
                href={`mailto:${who.email}`}
                className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-[12px] font-medium text-ink-700 shadow-sm ring-1 ring-ink-200/70 transition-colors hover:text-brand-600"
              >
                <Mail size={12} /> Email {who.userName ? who.userName.split(" ")[0] : "customer"}
              </a>
            )}
          </div>

          {/* Stored only as a validated loom.com share link, so swapping in
              /embed/ always gives a Loom player URL. */}
          {selected.loomUrl && (
            <div className="border-b border-ink-150 px-5 py-4">
              <div className="mb-2.5 flex items-center justify-between">
                <p className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-ink-800">
                  <Video size={13} className="text-violet-600" /> Screen recording
                </p>
                <a href={selected.loomUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11.5px] font-medium text-brand-600 hover:underline">
                  Open <ExternalLink size={11} />
                </a>
              </div>
              <div className="aspect-video overflow-hidden rounded-xl bg-ink-900 shadow-sm ring-1 ring-ink-200/70">
                <iframe
                  src={selected.loomUrl.replace("/share/", "/embed/")}
                  title={`Loom video for ticket #${selected.number}`}
                  className="h-full w-full"
                  allow="fullscreen; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          )}

          <div className="border-b border-ink-150 px-5 py-4">
            <p className="mb-2 text-[12px] font-semibold text-ink-800">Priority</p>
            <div className="grid grid-cols-4 gap-1 rounded-xl bg-ink-100/80 p-1" role="radiogroup" aria-label="Priority">
              {PRIORITIES.map((p) => {
                const on = (selected.priority || "normal") === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => !on && update({ priority: p.id }, `Priority set to ${p.label}`)}
                    className={cx(
                      "rounded-lg py-1.5 text-[11.5px] font-medium transition-all",
                      on ? cx("bg-white shadow-sm", p.tone) : "text-ink-500 hover:text-ink-800",
                    )}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="px-5 py-3">
            <Row icon={MapPin} label="Account">
              {selected.locationName || selected.companyName || "—"}
              <p className="break-all font-mono text-[11px] text-ink-400">{selected.locationId || selected.companyId}</p>
            </Row>
            <Row icon={Package} label="App">
              {selected.appName}
            </Row>
            <Row icon={LifeBuoy} label="Opened">
              {formatDate(selected.createdAt)} at {clock(selected.createdAt)}
            </Row>
            <Row icon={MessagesSquare} label="Conversation">
              {selected.messageCount} message{selected.messageCount === 1 ? "" : "s"} · last {dayLabel(selected.lastMessageAt).toLowerCase() === "today" ? "today" : dayLabel(selected.lastMessageAt)} at {clock(selected.lastMessageAt)}
            </Row>
          </div>
        </aside>
      )}
    </div>
  );
};

export default Support;
